import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCrown,
  faCalendarAlt,
  faCalendarCheck,
  faCreditCard,
  faReceipt,
  faRotate,
  faFileInvoice,
  faArrowRight
} from '@fortawesome/free-solid-svg-icons';

import conti from '../imagesHome/conti.png';

import { formatRoDate } from '../premium/formatRoDate';
import { formatLeiExact } from '../premium/money';

import './DetaliiPlanCard.css';

const apiUrl = process.env.REACT_APP_API_URL;

const NOT_AVAILABLE = 'Nedisponibil';

const CARD_BRANDS = {
  visa: 'Visa',
  mastercard: 'Mastercard',
  amex: 'American Express',
  maestro: 'Maestro',
  discover: 'Discover',
  diners: 'Diners Club',
  jcb: 'JCB',
  unionpay: 'UnionPay'
};

const planName = (subscription) => {
  if (subscription.status === 'none') return 'Gratuit';
  const months = subscription.plan?.durationMonths;
  if (months === 1) return 'Premium · 1 luna';
  if (months > 1) return `Premium · ${months} luni`;
  return 'Premium';
};

const paymentMethod = (payment) => {
  if (!payment?.card_brand && !payment?.card_last4) return NOT_AVAILABLE;
  const brand = CARD_BRANDS[payment.card_brand] || payment.card_brand || 'Card';
  return payment.card_last4 ? `${brand} •••• ${payment.card_last4}` : brand;
};

const lastPayment = (payment) => {
  if (!payment) return NOT_AVAILABLE;
  const amount = payment.currency === 'RON' ? formatLeiExact(payment.amount) : `${payment.amount / 100} ${payment.currency}`;
  return `${amount} · ${formatRoDate(payment.paid_at)}`;
};

const ACTION_LABELS = {
  active: 'Prelungeste abonamentul',
  expired: 'Reinnoieste abonamentul',
  none: 'Vezi planurile Premium'
};

// Setari -> Abonament: datele vin din GET /premium/subscription (users + comenzile platite).
const DetaliiPlanCard = () => {
  const [subscription, setSubscription] = useState(null);
  const [loadError, setLoadError] = useState(false);

  const load = useCallback(async (isCancelled = () => false) => {
    setLoadError(false);
    try {
      const response = await fetch(`${apiUrl}/premium/subscription`, { credentials: 'include' });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      if (!isCancelled()) setSubscription(data);
    } catch (err) {
      console.error('Eroare la incarcarea abonamentului:', err);
      if (!isCancelled()) setLoadError(true);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    load(() => cancelled);
    return () => {
      cancelled = true;
    };
  }, [load]);

  if (!subscription) {
    return (
      <section className="cl-plan" aria-label="Detalii abonament" aria-busy={!loadError}>
        <div className="cl-plan__heading">
          <h2>Planul tau</h2>
          {loadError ? (
            <p role="alert">Nu am putut incarca abonamentul.</p>
          ) : (
            <p>Se Incarca abonamentul…</p>
          )}
        </div>
        {loadError && (
          <div className="cl-plan__actions">
            <button type="button" className="cl-plan__secondary-btn" onClick={() => load()}>
              IncearcA din nou
            </button>
          </div>
        )}
      </section>
    );
  }

  const { status } = subscription;
  const payment = subscription.last_payment;

  const details = [
    {
      label: 'Data de start',
      value: formatRoDate(subscription.started_at) || NOT_AVAILABLE,
      icon: faCalendarAlt
    },
    {
      label: status === 'expired' ? 'A expirat la' : 'Valabil până la',
      value: formatRoDate(subscription.premium_expires_at) || NOT_AVAILABLE,
      icon: faCalendarCheck
    },
    {
      label: 'Reînnoire',
      value: 'Manuală, fără plăți automate',
      icon: faRotate
    },
    {
      label: 'Metoda de plată',
      value: paymentMethod(payment),
      icon: faCreditCard
    },
    {
      label: 'Ultima plată',
      value: lastPayment(payment),
      icon: faReceipt
    }
  ];

  return (
    <section className="cl-plan" aria-label="Detalii abonament">
      <div className="cl-plan__heading">
        <h2>Planul tau</h2>
        
      </div>

      <div className="cl-plan__summary">
        <div className="cl-plan__summary-left">
          
<span className="cl-plan__crown">
  <img
    src={conti}
    alt="Conti"
    className="cl-plan__conti"
  />
</span>


          <div>
            <span className="cl-plan__eyebrow">PLANUL TAU</span>
            <strong className="cl-plan__name">{planName(subscription)}</strong>
          </div>
        </div>

        {status === 'active' && <span className="cl-plan__status">Activ</span>}
        {status === 'expired' && (
          <span className="cl-plan__status cl-plan__status--expired">Expirat</span>
        )}
      </div>

      {status === 'none' ? (
        <p className="cl-plan__empty">
          Nu ai un abonament Premium. Cu Premium ai acces la toate cursurile ContaLearn si
          vieti nelimitate.
        </p>
      ) : (
        <div className="cl-plan__details">
          <h3>Informatii abonament</h3>

          {details.map((item) => (
            <div className="cl-plan__detail" key={item.label}>
              <span className="cl-plan__detail-icon">
                <FontAwesomeIcon icon={item.icon} />
              </span>

              <div className="cl-plan__detail-copy">
                <span className="cl-plan__detail-label">{item.label}</span>
                <span className="cl-plan__detail-value">{item.value}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="cl-plan__actions">
        <Link to="/premium/plans" className="cl-plan__primary-btn">
          {ACTION_LABELS[status] || ACTION_LABELS.none}
          <FontAwesomeIcon icon={faArrowRight} />
        </Link>

        {payment?.receipt_url && (
          <a
            className="cl-plan__secondary-btn"
            href={payment.receipt_url}
            target="_blank"
            rel="noopener noreferrer"
          >
            <FontAwesomeIcon icon={faFileInvoice} />
            Vezi chitanța
          </a>
        )}
      </div>
    </section>
  );
};

export default DetaliiPlanCard;
